import { useCallback, useEffect, useState } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ArrowDownRight, ArrowUpRight, Receipt, Wallet, FileText,
  BarChart3, Target, Tags, FileSpreadsheet, Download, Settings, Search, Plus, LogOut, MonitorPlay,
  ChevronDown, EyeOff, Users, ShieldCheck, ScrollText, UserCircle, Menu, X, ShieldAlert,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatRupiah } from "../lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import TransactionModal from "./TransactionModal";

const NAV = [
  {
    group: "UTAMA",
    items: [
      { to: "/", key: "dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, perm: "view_dashboard" },
      { to: "/pemasukan", key: "pemasukan", label: "Pemasukan", icon: ArrowDownRight, perm: "view_income" },
      { to: "/pengeluaran", key: "pengeluaran", label: "Pengeluaran", icon: ArrowUpRight, perm: "view_expense" },
      { to: "/transaksi", key: "semua-transaksi", label: "Semua Transaksi", icon: Receipt, perm: "view_transactions" },
    ],
  },
  {
    group: "KAS & ANALISIS",
    items: [
      { to: "/saldo", key: "saldo-kas", label: "Saldo Kas", icon: Wallet, perm: "view_balance" },
      { to: "/laporan", key: "laporan", label: "Laporan", icon: FileText, perm: "view_reports" },
      { to: "/rekap", key: "rekap-bulanan", label: "Rekap Bulanan", icon: BarChart3, perm: "view_reports" },
      { to: "/anggaran", key: "anggaran", label: "Anggaran", icon: Target, perm: "view_budget" },
    ],
  },
  {
    group: "MANAJEMEN & TOOLS",
    items: [
      { to: "/kategori", key: "kategori", label: "Kategori", icon: Tags, perm: "manage_categories" },
      { to: "/import", key: "import-excel", label: "Import Excel", icon: FileSpreadsheet, perm: "import_excel" },
      { to: "/export", key: "export-data", label: "Export", icon: Download, perm: ["export_excel", "export_reports"] },
      { to: "/pengaturan", key: "pengaturan", label: "Pengaturan", icon: Settings, perm: "manage_settings" },
    ],
  },
  {
    group: "ADMINISTRASI",
    items: [
      { to: "/pengguna", key: "pengguna", label: "Manajemen Pengguna", icon: Users, perm: "view_users" },
      { to: "/roles", key: "roles", label: "Role & Permission", icon: ShieldCheck, perm: "manage_roles" },
      { to: "/audit", key: "audit", label: "Audit Trail", icon: ScrollText, perm: "view_audit_log" },
    ],
  },
];

const TITLES = {
  "/": "Dashboard",
  "/pemasukan": "Pemasukan",
  "/pengeluaran": "Pengeluaran",
  "/transaksi": "Semua Transaksi",
  "/saldo": "Saldo Kas",
  "/laporan": "Laporan Keuangan",
  "/rekap": "Rekap Bulanan",
  "/anggaran": "Anggaran Bulanan",
  "/kategori": "Master Kategori",
  "/import": "Import Excel",
  "/export": "Export Data",
  "/pengaturan": "Pengaturan",
  "/khusus-admin": "Pengeluaran Khusus",
  "/pengguna": "Manajemen Pengguna",
  "/roles": "Role & Permission",
  "/audit": "Audit Trail",
  "/profil": "Profil Saya",
};

const fmtLastSeen = (iso) => {
  if (!iso) return "-";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "baru saja";
  return `${Math.floor(s / 60)} mnt lalu`;
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [books, setBooks] = useState(null);
  const [q, setQ] = useState("");
  const [modalType, setModalType] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );
  const [showSpecial, setShowSpecial] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showOnlineDetail, setShowOnlineDetail] = useState(false);
  const [minAlert, setMinAlert] = useState(0);

  useEffect(() => {
    const p = user?.permissions || [];
    if (!p.includes("view_users")) return;
    const load = () => api.get("/users/online").then((r) => setOnlineUsers(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const load = () =>
      api.get("/settings").then((r) => {
        setShowSpecial(Boolean(r.data.show_special_menu));
        setMinAlert(Number(r.data.min_balance_alert) || 0);
      }).catch(() => {});
    load();
    window.addEventListener("settings:refresh", load);
    return () => window.removeEventListener("settings:refresh", load);
  }, []);

  const refreshSaldo = useCallback(() => {
    api.get("/stats/books")
      .then((r) => setBooks(r.data))
      .catch(() => setBooks(null));
  }, []);

  useEffect(() => {
    refreshSaldo();
  }, [location.pathname, modalType, refreshSaldo]);

  useEffect(() => {
    window.addEventListener("saldo:refresh", refreshSaldo);
    return () => window.removeEventListener("saldo:refresh", refreshSaldo);
  }, [refreshSaldo]);

  // tutup sidebar saat navigasi di layar kecil
  useEffect(() => {
    if (window.innerWidth < 1024) setSidebarOpen(false);
  }, [location.pathname]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/transaksi?q=${encodeURIComponent(q.trim())}`);
  };

  const perms = user?.permissions || [];
  const allowed = (perm) => !perm || [].concat(perm).some((p) => perms.includes(p));
  const navSections = NAV.map((s) => ({ ...s, items: [...s.items] }));
  if (showSpecial && allowed("view_special_expense")) {
    navSections.find((s) => s.group === "ADMINISTRASI")?.items.unshift({
      to: "/khusus-admin", key: "khusus-admin", label: "Pengeluaran Khusus",
      icon: EyeOff, perm: "view_special_expense",
    });
  }
  const title = TITLES[location.pathname] || "Dashboard";
  const lowBalance = [];
  if (minAlert > 0 && books) {
    if (perms.includes("view_saldo_resmi") && books.resmi != null && books.resmi < minAlert)
      lowBalance.push(`Saldo Kas Resmi ${formatRupiah(books.resmi)}`);
    if (perms.includes("view_saldo_imf") && books.imf != null && books.imf < minAlert)
      lowBalance.push(`Saldo Kas IMF ${formatRupiah(books.imf)}`);
  }

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        data-testid="sidebar-navigation"
        className={`fixed inset-y-0 left-0 w-[260px] bg-[#0F172A] border-r border-slate-800 flex flex-col z-40 transform transition-transform duration-200 overflow-hidden ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <img
          src="/assets/logo-imf.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute bottom-20 left-1/2 -translate-x-1/2 w-44 opacity-[0.05]"
        />
        <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-800">
          <img
            src="/assets/logo-imf.png"
            alt="Logo IMF-831"
            className="w-9 h-9 sm:w-11 sm:h-11 object-contain"
          />
          <div className="flex-1">
            <p className="text-white font-extrabold tracking-tight leading-tight">IMF-831</p>
            <p className="text-[11px] text-slate-400">Laporan Kas & Keuangan</p>
          </div>
          <button
            data-testid="btn-close-sidebar"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {navSections.map((section) => {
            const items = section.items.filter((i) => allowed(i.perm));
            if (!items.length) return null;
            return (
              <div key={section.group}>
                <p className="px-3 mb-1.5 text-[10px] font-bold tracking-widest text-slate-400">
                  {section.group}
                </p>
                <div className="space-y-0.5">
                  {items.map((item) => (
                    <NavLink
                      key={item.key}
                      to={item.to}
                      end={item.end}
                      data-testid={`nav-item-${item.key}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                          isActive
                            ? "bg-blue-600/15 text-cyan-400 font-semibold border-l-2 border-cyan-400"
                            : "text-slate-400 hover:text-white hover:bg-slate-800/70"
                        }`
                      }
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="relative z-10 bg-[#0F172A] p-4 border-t border-slate-800">
          {perms.includes("view_users") && (
            <div className="mb-3" data-testid="sidebar-online-monitor">
              <button
                data-testid="btn-toggle-online-detail"
                onClick={() => setShowOnlineDetail((v) => !v)}
                className="w-full text-[10px] font-bold tracking-widest text-slate-400 uppercase mb-2 flex items-center justify-between hover:text-slate-100 transition-colors"
              >
                Monitoring Online
                <span className="flex items-center gap-1.5">
                  <span className="font-num text-cyan-400">{onlineUsers.length}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${showOnlineDetail ? "rotate-180" : ""}`} />
                </span>
              </button>
              {onlineUsers.length === 0 ? (
                <p className="text-[10px] text-slate-400">Tidak ada akun online</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {onlineUsers.map((u) => (
                    <div
                      key={u.id}
                      data-testid={`online-user-${u.id}`}
                      title={`${u.name} · ${u.role_label}`}
                      className="relative w-7 h-7 rounded-full bg-slate-700 text-slate-100 flex items-center justify-center text-[10px] font-bold border border-cyan-400/40"
                    >
                      {(u.name || "?")[0].toUpperCase()}
                      <span className="led-dot absolute -bottom-0.5 -right-0.5" style={{ width: 7, height: 7 }} />
                    </div>
                  ))}
                </div>
              )}
              {showOnlineDetail && onlineUsers.length > 0 && (
                <div className="mt-2 space-y-1.5 rounded-lg border border-slate-800 bg-slate-900 p-2" data-testid="online-detail-list">
                  {onlineUsers.map((u) => (
                    <div key={u.id} className="flex items-center gap-2" data-testid={`online-detail-${u.id}`}>
                      <span className="led-dot shrink-0" style={{ width: 6, height: 6 }} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-slate-100 truncate">{u.name}</p>
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider truncate">{u.role_label}</p>
                      </div>
                      <span className="text-[9px] font-num text-slate-400 shrink-0">{fmtLastSeen(u.last_seen)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mb-2.5 px-1" data-testid="sidebar-status">
            <span className="led-dot" />
            <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Sistem Aktif · IMF-831</span>
          </div>
          <div className="rounded-lg bg-slate-800 border border-slate-800 p-3 space-y-2.5" data-testid="sidebar-saldo-badge">
            {perms.includes("view_saldo_resmi") && (
              <div data-testid="sidebar-saldo-resmi">
                <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Saldo Kas Resmi</p>
                <p className="font-num text-base font-bold text-cyan-400 mt-0.5">
                  {books?.resmi != null ? formatRupiah(books.resmi) : "…"}
                </p>
              </div>
            )}
            {perms.includes("view_saldo_imf") && (
              <div data-testid="sidebar-saldo-imf" className={perms.includes("view_saldo_resmi") ? "pt-2.5 border-t border-slate-800" : ""}>
                <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Saldo Kas IMF</p>
                <p className="font-num text-base font-bold text-emerald-400 mt-0.5">
                  {books?.imf != null ? formatRupiah(books.imf) : "…"}
                </p>
              </div>
            )}
          </div>
        </div>
      </aside>

      <div
        className={`flex flex-col min-h-screen transition-[margin] duration-200 ${
          sidebarOpen ? "lg:ml-[260px]" : ""
        }`}
      >
        <div className="h-[3px] bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-600 sticky top-0 z-30" />
        <header className="sticky top-[3px] z-20 bg-[#0F172A]/80 backdrop-blur-xl border-b border-slate-800 px-4 lg:px-6 py-3 flex items-center gap-3">
          <button
            data-testid="btn-toggle-sidebar"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-slate-100"
            title="Buka/Tutup Menu"
          >
            <Menu className="w-4 h-4" />
          </button>
          <h1 className="text-lg font-bold tracking-tight text-slate-100 whitespace-nowrap truncate max-w-[34vw] sm:max-w-none">
            {title}
          </h1>
          <form onSubmit={onSearch} className="flex-1 max-w-md ml-2 hidden sm:block">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="input-global-search"
                className="w-full rounded-lg border border-slate-700 bg-[#1E293B] pl-9 pr-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400 transition-all"
                placeholder="Cari uraian, no bukti, kategori…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </form>
          <div className="flex-1" />
          {(allowed("create_income") || allowed("create_expense")) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="btn-quick-add"
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold px-3 lg:px-4 py-2 rounded-lg transition-all flex items-center gap-2 text-sm shadow-lg shadow-blue-500/25"
                >
                  <Plus className="w-4 h-4" /> <span className="hidden md:inline">Transaksi Baru</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {allowed("create_income") && (
                  <DropdownMenuItem data-testid="quick-add-income" onClick={() => setModalType("income")}>
                    <ArrowDownRight className="w-4 h-4 mr-2 text-emerald-400" /> Pemasukan Baru
                  </DropdownMenuItem>
                )}
                {allowed("create_expense") && (
                  <DropdownMenuItem data-testid="quick-add-expense" onClick={() => setModalType("expense")}>
                    <ArrowUpRight className="w-4 h-4 mr-2 text-rose-400" /> Pengeluaran Baru
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            data-testid="btn-display-mode"
            onClick={() => window.open("/display", "_blank")}
            title="Buka Mode Display (layar monitoring)"
            className="p-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-slate-100 transition-colors"
          >
            <MonitorPlay className="w-4 h-4" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-profile-menu"
                className="flex items-center gap-2 rounded-lg border border-slate-800 px-3 py-1.5 hover:bg-slate-800 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                  {(user?.name || "A")[0].toUpperCase()}
                </div>
                <div className="text-left hidden lg:block">
                  <p className="text-xs font-semibold text-slate-100 leading-tight">{user?.name || "Pengguna"}</p>
                  <p className="text-[10px] text-slate-400 leading-tight">{user?.role_label || ""}</p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-semibold text-slate-100">{user?.name}</p>
                <p className="text-xs text-slate-400">{user?.email}</p>
                <span className="inline-block mt-1 bg-slate-800 text-slate-400 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                  {user?.role_label}
                </span>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="btn-profile" onClick={() => navigate("/profil")}>
                <UserCircle className="w-4 h-4 mr-2" /> Profil Saya
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="btn-logout"
                className="text-rose-400 focus:text-rose-400"
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
              >
                <LogOut className="w-4 h-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {user?.using_default_password && (
          <div
            data-testid="default-password-warning"
            className="bg-amber-500/10 border-b border-amber-500/30 text-amber-300 text-xs px-4 lg:px-6 py-2 flex items-center gap-2"
          >
            <ShieldAlert className="w-4 h-4 shrink-0" />
            <span>
              Demi keamanan, Anda masih menggunakan kata sandi bawaan.{" "}
              <Link to="/profil" className="font-bold underline">
                Ganti sekarang di Profil Saya
              </Link>
            </span>
          </div>
        )}

        {lowBalance.length > 0 && (
          <div
            data-testid="low-balance-warning"
            className="bg-rose-500/15 border-b border-rose-500/30 text-rose-800 text-xs px-4 lg:px-6 py-2 flex items-center gap-2"
          >
            <ShieldAlert className="w-4 h-4 shrink-0" />
            <span>
              <span className="font-bold">Peringatan saldo rendah:</span> {lowBalance.join(" · ")} —
              di bawah batas aman {formatRupiah(minAlert)}. Atur batas di menu Pengaturan.
            </span>
          </div>
        )}

        <main key={location.pathname} className="flex-1 p-4 lg:p-6 fade-up" style={{ animationDelay: "0.05s" }}>
          <Outlet />
        </main>
      </div>

      {modalType && (
        <TransactionModal
          open={Boolean(modalType)}
          type={modalType}
          onOpenChange={(o) => !o && setModalType(null)}
          onSaved={() => setModalType(null)}
        />
      )}
    </div>
  );
}

export function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0F19]">
      <div className="w-8 h-8 border-4 border-slate-800 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );
}
